import {
  type OverlayDrawContext,
  type OverlayLayerEntry,
  type OverlayPaintContext,
  type OverlayPlane,
} from './type';

/** 정렬 후 실제로 paint 할 단위. priority + z 로 정렬에 쓰인다. */
export type OverlayDrawUnit = {
  /** 레이어의 `isPaintStateSelfContained` 선언입니다. collect 빌보드 단위에만 켜질 수 있습니다. */
  isPaintStateSelfContained: boolean;
  /** 이 단위를 만든 레이어. paint 중 기록되는 판정 대상의 칸 이름입니다. */
  layerId: string;
  /** 레이어 식별자(`_order`) 겸 정렬 tiebreak. */
  order: number;
  paint: (context: OverlayPaintContext) => void;
  priority: number;
  z: number;
};

/**
 * 단위 객체 풀과 반환 배열입니다. 프레임마다 단위 수천 개를 새로 만들지 않도록 재사용합니다.
 * 모듈 하나가 모든 엔진에서 공유되므로 "정렬 → paint 가 동기로 끝난 뒤에만 다음 정렬을 한다" 는
 * 불변식에 기댑니다. 엔진의 평면 도장이 이 순서를 지키고, 엔진이 여러 개여도 도장 함수는 한 번에
 * 하나만 실행됩니다. 정렬 결과를 보관했다가 나중에 paint 하는 호출자를 만들면 안 됩니다.
 */
const drawUnitPool: OverlayDrawUnit[] = [];
const drawUnitBuffer: OverlayDrawUnit[] = [];

const pushDrawUnit = (
  isPaintStateSelfContained: boolean,
  layerId: string,
  order: number,
  paint: OverlayDrawUnit['paint'],
  priority: number,
  z: number,
) => {
  const index = drawUnitBuffer.length;
  let unit = drawUnitPool[index];
  if (!unit) {
    unit = { isPaintStateSelfContained, layerId, order, paint, priority, z };
    drawUnitPool[index] = unit;
  } else {
    unit.isPaintStateSelfContained = isPaintStateSelfContained;
    unit.layerId = layerId;
    unit.order = order;
    unit.paint = paint;
    unit.priority = priority;
    unit.z = z;
  }
  drawUnitBuffer.push(unit);
};

/**
 * 레이어 목록을 "밴드+깊이" 정렬 기준으로 OverlayDrawUnit 배열로 변환한다.
 * - draw 레이어: z=0 으로 단일 unit 생성 (priority 로만 정렬).
 * - collect 레이어: 반환된 빌보드마다 unit 생성, z 내림차순으로 섞임.
 * - 정렬: priority 오름차순 → z 내림차순 → _order 오름차순.
 * - `plane` 이 일치하는 레이어만 포함한다 (레이어 생략 시 `'top'`).
 *
 * 반환 배열과 그 안의 단위 객체는 재사용됩니다. 다음 호출 전까지만 유효하므로 보관하면 안 됩니다.
 */
export const getSortedDrawUnit = (
  layerList: OverlayLayerEntry[],
  drawContext: OverlayDrawContext,
  plane: OverlayPlane = 'top',
): OverlayDrawUnit[] => {
  drawUnitBuffer.length = 0;
  for (const layer of layerList) {
    if (!layer.enabled) continue;
    if ((layer.plane ?? 'top') !== plane) continue;
    if (layer.draw) {
      // draw 레이어는 레이어 전체가 하나의 단위(z=0)로 들어간다.
      // draw 레이어는 collect 빌보드와 다른 priority 밴드에 두어야 한다 —
      // 같은 밴드에 두면 z=0(near/far 중간 깊이)이 빌보드들 사이에 끼어
      // 의도치 않은 순서가 된다.
      // (현재 분리: 편집 UI=55/60/80, 빌보드=40)
      pushDrawUnit(
        false,
        layer.id,
        layer._order,
        layer.draw,
        layer.priority,
        0,
      );
    }
    if (layer.collect) {
      const isPaintStateSelfContained =
        layer.isPaintStateSelfContained === true;
      for (const billboard of layer.collect(drawContext)) {
        pushDrawUnit(
          isPaintStateSelfContained,
          layer.id,
          layer._order,
          billboard.paint,
          layer.priority,
          billboard.z,
        );
      }
    }
  }
  drawUnitBuffer.sort(
    (a, b) => a.priority - b.priority || b.z - a.z || a.order - b.order,
  );
  return drawUnitBuffer;
};

/**
 * 정렬된 단위를 순서대로 paint 합니다. 단위마다 `save`/`restore` 로 감싸되,
 * `isPaintStateSelfContained` 단위가 연속되는 구간은 구간 전체를 1회만 감쌉니다.
 * 선언의 뜻은 `OverlayLayerOption.isPaintStateSelfContained` 주석과 같습니다(자기가 쓰는 상태를
 * 매번 전부 다시 설정하고, 변환·클립·globalAlpha·합성 모드·그림자·선 끝 모양·현재 경로에 의존하지
 * 않음). 그래서 레이어나 priority 밴드가 달라도 선언된 단위끼리는 상태가 새도 결과가 같습니다.
 * 선언을 잘못 켠 레이어는 같은 구간에 섞인 다른 레이어의 카드까지 망치므로, 선언은 그리기 함수가
 * 위 조건을 지킨다는 것을 spec 으로 확인한 뒤에만 켭니다. 정렬 순서와 paint 호출 순서는 바뀌지 않습니다.
 *
 * `onBeforePaint` 는 단위마다 paint 직전에 불립니다. 엔진은 여기서 판정 대상을 기록할 레이어 칸을
 * 바꿉니다. paint 가 throw 해도 열어 둔 `save` 는 `restore` 로 닫습니다.
 */
export const paintDrawUnitList = (
  unitList: readonly OverlayDrawUnit[],
  paintContext: OverlayPaintContext,
  onBeforePaint?: (unit: OverlayDrawUnit) => void,
) => {
  const { ctx } = paintContext;
  let index = 0;
  while (index < unitList.length) {
    const first = unitList[index];
    let end = index + 1;
    if (first.isPaintStateSelfContained) {
      while (end < unitList.length && unitList[end].isPaintStateSelfContained) {
        end += 1;
      }
    }

    ctx.save();
    try {
      for (let i = index; i < end; i += 1) {
        onBeforePaint?.(unitList[i]);
        unitList[i].paint(paintContext);
      }
    } finally {
      ctx.restore();
    }
    index = end;
  }
};

/**
 * 평면 도장마다 1씩 커지는 식별자입니다. `OverlayDrawContext.paintSerial` 로 전달됩니다.
 * 모든 엔진이 하나의 값을 공유하므로 서로 다른 엔진의 도장이 같은 값을 갖지 않습니다.
 */
let overlayPaintSerial = 0;

/** 새 평면 도장의 식별자를 발급합니다. */
export const nextOverlayPaintSerial = () => {
  overlayPaintSerial += 1;
  return overlayPaintSerial;
};

/** 마지막으로 발급한 평면 도장 식별자입니다. hit-test 컨텍스트에 그대로 싣습니다. */
export const getOverlayPaintSerial = () => overlayPaintSerial;
